import { SingleDatePicker } from "@/components/SingleDatePicker";
import { TimePicker } from "@/components/TimePicker";
import { useAppointmentConfig } from "@/features/appointments/utils";
import { useModals } from "@/features/modals/hooks";
import { type hfb, isTeamSelection, useTeam } from "@repo/api";
import { FormField, ListView, Option, Select } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoworkerAndTeamSelectorListItem,
  ElementsContainer,
  SectionContainer,
  TwoColumnElementsContainer,
  translateHFB,
  translateServices,
  useSettings,
} from "@repo/ui";
import { combineDates, formatDateToApi, isDefined } from "@repo/utils";
import { isBefore } from "date-fns";
import { useCallback, useEffect, useState } from "react";

import {
  type AppointmentModalState,
  type AppointmentModalStateActions,
} from "./AppointmentModalReducer";

export const DropInAppointmentEdit = ({
  state,
  actions,
}: {
  state: AppointmentModalState;
  actions: AppointmentModalStateActions;
}) => {
  const { pushModal } = useModals();
  const { market, boka_config } = useSettings();
  const editableAppointment = state.updatedAppointment ?? state.appointment;

  const inferDepartmentFromService = useCallback(
    (serviceKey?: string): hfb | undefined => {
      if (!serviceKey) {
        return undefined;
      }

      const fromHfbConfig = Object.entries(boka_config?.hfb ?? {}).find(
        ([, value]) =>
          value.service_areas
            ?.flatMap((area) => area.services ?? [])
            .some((service) => service?.name_key === serviceKey)
      )?.[0] as hfb | undefined;

      if (fromHfbConfig) {
        return fromHfbConfig;
      }

      return boka_config?.hfb_configuration?.hfb_list?.find((hfbItem) =>
        hfbItem.service_areas?.some((area) =>
          area.services?.some((service) => service.name_key === serviceKey)
        )
      )?.hfb_code_key;
    },
    [boka_config]
  );

  const inferredDepartment =
    editableAppointment?.serviceAreas ??
    inferDepartmentFromService(editableAppointment?.service);

  const { cmps, hfbs, services } = useAppointmentConfig(
    editableAppointment?.locationName,
    inferredDepartment
  );
  const { data: assignedTeamData } = useTeam(editableAppointment?.assignedTeam);
  const teamSelection = assignedTeamData
    ? {
        kind: "team" as const,
        team: assignedTeamData,
        assignedToTeam: true,
        members: (assignedTeamData.teamMembers ?? []).map((m) => ({
          ...m,
          selected: true,
        })),
      }
    : undefined;

  const [startDate, setStartDate] = useState<Date | undefined>(
    editableAppointment?.timeWindowStartTime
      ? new Date(editableAppointment.timeWindowStartTime)
      : undefined
  );
  const [startTime, setStartTime] = useState<Date | undefined>(
    editableAppointment?.timeWindowStartTime
      ? new Date(editableAppointment.timeWindowStartTime)
      : undefined
  );

  const [endTime, setEndTime] = useState<Date | undefined>(
    editableAppointment?.timeWindowEndTime
      ? new Date(editableAppointment.timeWindowEndTime)
      : undefined
  );
  const [department, setDepartment] = useState<hfb | undefined>(
    inferredDepartment
  );
  const [service, setService] = useState<string | undefined>(
    editableAppointment?.service
  );
  const [cmp, setCmp] = useState<{
    code: string;
    name: string;
  }>({
    code: editableAppointment?.locationName || "",
    name: editableAppointment?.locationType || "",
  });

  useEffect(() => {
    if (!editableAppointment) {
      return;
    }

    setStartDate(
      editableAppointment.timeWindowStartTime
        ? new Date(editableAppointment.timeWindowStartTime)
        : undefined
    );
    setStartTime(
      editableAppointment.timeWindowStartTime
        ? new Date(editableAppointment.timeWindowStartTime)
        : undefined
    );
    setEndTime(
      editableAppointment.timeWindowEndTime
        ? new Date(editableAppointment.timeWindowEndTime)
        : undefined
    );
    setDepartment(
      editableAppointment.serviceAreas ??
        inferDepartmentFromService(editableAppointment.service)
    );
    setService(editableAppointment.service);
    setCmp({
      code: editableAppointment.locationName || "",
      name: editableAppointment.locationType || "",
    });
  }, [editableAppointment, inferDepartmentFromService]);

  const syncUpdatedAppointment = ({
    nextStartDate = startDate,
    nextStartTime = startTime,
    nextEndTime = endTime,
    nextDepartment = department,
    nextService = service,
    nextCmp = cmp,
  }: {
    nextStartDate?: Date;
    nextStartTime?: Date;
    nextEndTime?: Date;
    nextDepartment?: hfb;
    nextService?: string;
    nextCmp?: { code: string; name: string };
  } = {}) => {
    const sourceAppointment = state.updatedAppointment ?? state.appointment;

    if (!sourceAppointment?.id) {
      return;
    }

    const updates: Partial<typeof sourceAppointment> = {
      serviceAreas: nextDepartment ?? sourceAppointment.serviceAreas,
      service: nextService ?? sourceAppointment.service,
      locationName: nextCmp?.code || sourceAppointment.locationName,
      locationType: nextCmp?.name || sourceAppointment.locationType,
      countryCode: market.toUpperCase(),
    };

    if (nextStartDate && nextStartTime) {
      updates.timeWindowStartTime = formatDateToApi(
        combineDates(nextStartDate, nextStartTime)
      );
    }

    if (nextStartDate && nextEndTime) {
      updates.timeWindowEndTime = formatDateToApi(
        combineDates(nextStartDate, nextEndTime)
      );
    }

    actions.setUpdatedAppointment({
      ...sourceAppointment,
      ...updates,
    });
  };

  return (
    <SectionContainer>
      <ListView id="drop-in-assign">
        <CoworkerAndTeamSelectorListItem
          selectionMode={{
            coworkerSelectionMode: "SINGLE",
            selectOnlyTeam: true,
          }}
          title={t("label.assigned_to")}
          pushModal={pushModal}
          selected={
            teamSelection ??
            [editableAppointment?.assignedCoworker].filter(isDefined)
          }
          searchContext="CALENDAR"
          setSelected={(selected) => {
            const sourceAppointment =
              state.updatedAppointment ?? state.appointment;
            if (sourceAppointment) {
              if (isTeamSelection(selected)) {
                actions.setUpdatedAppointment({
                  ...sourceAppointment,
                  assignedTeam: selected.team.teamId,
                  assignedCoworker: undefined,
                });
              } else {
                actions.setUpdatedAppointment({
                  ...sourceAppointment,
                  assignedCoworker: selected?.[0]?.emailId,
                  assignedTeam: undefined,
                });
              }
            }
          }}
        />
      </ListView>
      <ElementsContainer>
        <FormField
          shouldValidate={!startDate}
          valid={Boolean(startDate)}
          validation={{
            msg: t("prompt.invalid_date"),
            id: "error",
            type: "error",
          }}
        >
          <SingleDatePicker
            id="start-date"
            label={t("label.start_date")}
            value={startDate}
            onChange={(nextDate) => {
              setStartDate(nextDate);
              syncUpdatedAppointment({ nextStartDate: nextDate });
            }}
            minDate={new Date()}
          />
        </FormField>

        <TwoColumnElementsContainer>
          <TimePicker
            id="start-time"
            label={t("label.start_time")}
            value={startTime}
            onChange={(nextTime) => {
              setStartTime(nextTime);
              syncUpdatedAppointment({ nextStartTime: nextTime });
            }}
            timeIntervals={5}
            minTime={new Date()}
            isValid={startTime !== undefined}
          />
          <TimePicker
            id="end-time"
            label={t("label.end_time")}
            value={endTime}
            onChange={(nextTime) => {
              setEndTime(nextTime);
              syncUpdatedAppointment({ nextEndTime: nextTime });
            }}
            timeIntervals={5}
            minTime={startTime}
            isValid={startTime && endTime && isBefore(startTime, endTime)}
          />
        </TwoColumnElementsContainer>

        <FormField shouldValidate={!cmp.code} valid={Boolean(cmp.code)}>
          <Select
            id="drop-in-cmp-select"
            label={t("label.cmp")}
            value={cmp.code}
            onChange={(e) => {
              const selectedCmp = cmps?.find(
                (c) => c.unit_id_key == e.target.value
              );
              const code = e.target.value;
              const nextCmp = { code, name: selectedCmp?.name ?? "" };
              setCmp(nextCmp);
              syncUpdatedAppointment({ nextCmp });
            }}
          >
            {cmps?.map((el) => (
              <Option
                key={el.name}
                value={el.unit_id_key}
                name={`${el.name} ${el.unit_id_key ? `(${el.unit_id_key})` : ""}`}
              />
            ))}
          </Select>
        </FormField>

        <FormField
          shouldValidate={department === undefined}
          valid={department !== undefined}
        >
          <Select
            id="drop-in-hfb-select"
            label={t("label.department")}
            value={department}
            onChange={(e) => {
              const nextDepartment = e.target.value as hfb;
              setDepartment(nextDepartment);
              syncUpdatedAppointment({ nextDepartment });
            }}
          >
            {hfbs?.map((el) => (
              <Option
                key={`drop-in-hfb-option-${el}`}
                value={el}
                name={translateHFB(el)}
              />
            ))}
          </Select>
        </FormField>
        <FormField
          shouldValidate={
            service === undefined || service === t("action.choose_an_option")
          }
          valid={
            service !== undefined && service !== t("action.choose_an_option")
          }
        >
          <Select
            id="drop-in-service-select"
            label={t("label.service")}
            value={service}
            onChange={(e) => {
              const nextService = e.target.value;
              setService(nextService);
              syncUpdatedAppointment({ nextService });
            }}
          >
            {services.map((el) => (
              <Option key={el} value={el} name={translateServices(el)} />
            ))}
          </Select>
        </FormField>
      </ElementsContainer>
    </SectionContainer>
  );
};
